<template>
  <div
    class="bg-form-box w-full xs:max-w-[400px] lg:max-w-[650px] lg:w-[55%] relative border border-[var(--color-border-subtle)] dark:border-slate-700/60 flex flex-col items-center gap-5 py-7 lg:py-[70px] rounded-xl shadow-lg overflow-hidden"
  >
    <BaseButton
      v-if="LANG_ENABLED === '1'"
      :is-icon-only="true"
      :is-circle="true"
      variant="outlines"
      @click="toggleLocale"
      class="absolute top-2 left-2"
    >
      <template #left>
        <component :is="IconLanguage" size="20"></component>
      </template>
    </BaseButton>
    <div class="overflow-hidden max-w-[70px] max-h-[70px]">
      <img :src="APP_LOGO" alt="logo" class="object-cover w-full h-full" fetchpriority="high" width="70" height="70" />
    </div>
    <h1 class="title xs:text-[20px] lg:text-[26px] leading-8 font-demiboldd tracking-tight text-center text-font-dark">
      {{ t('welcome') }}
    </h1>
    <p class="subtitle text-font-gray xs:text-[13px] lg:text-[14px] leading-6 text-center font-medium">
      {{ t('user.loginDesc') }}
    </p>
    <form
      @submit.prevent="formHandler"
      class="xs:w-[80%] lg:w-[60%] flex flex-col gap-4"
    >
      <!-- Username field -->
      <div class="flex flex-col gap-1.5">
        <label for="username" class="text-[13px] font-medium text-font-dark text-right">
          {{ t('user.username') }}
        </label>
        <div class="relative">
          <input
            dir="ltr"
            type="text"
            v-model="username"
            name="username"
            id="username"
            required
            @blur="validateUsername"
            class="input-base text-left"
            :class="usernameError ? 'input-error' : ''"
          />
        </div>
        <Transition name="fade-slide">
          <span v-if="usernameError" class="text-red-600 dark:text-red-400 text-[11px]">{{ usernameError }}</span>
        </Transition>
      </div>

      <!-- Password field -->
      <div class="flex flex-col gap-1.5">
        <label for="password" class="text-[13px] font-medium text-font-dark text-right">
          {{ t('user.password') }}
        </label>
        <div class="relative">
          <input
            dir="ltr"
            :type="isShowPass ? 'text' : 'password'"
            v-model="password"
            name="password"
            id="password"
            autocomplete="new-password"
            required
            @blur="validatePassword"
            class="input-base text-left pl-11"
            :class="passwordError ? 'input-error' : ''"
          />
          <BaseButton
            tag="button"
            type="button"
            :is-icon-only="true"
            class="absolute top-1/2 -translate-y-1/2 right-2 !p-1.5"
            variant="trans"
            @click="showPass"
          >
            <template #left>
              <IconEye v-if="!isShowPass" />
              <IconEyeClosed v-else />
            </template>
          </BaseButton>
        </div>
        <Transition name="fade-slide">
          <span v-if="passwordError" class="text-red-600 dark:text-red-400 text-[11px]">{{ passwordError }}</span>
        </Transition>
      </div>

     <BaseButton
        type="submit"
        :disabled="!isFormValid || isSubmitting"
        class="w-full !py-2.5 mt-1"
        :loading="isSubmitting"
        :style="{ backgroundColor: BASE_COLOR }"
      >
        {{ t('user.login') }}
      </BaseButton>

    </form>

<BaseModal
  v-model="auth.showOtpModal"
  size="md"
  showOtpInput
  :is-loading="isLoading"
  @complete="completeHandler"
/>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { IconEye, IconEyeClosed, IconLanguage } from '@tabler/icons-vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { useLocale } from '@/composables/useLocale'
import { useI18n } from 'vue-i18n'
import BaseModal from '@/components/base/BaseModal.vue'
import { useAuthLogin } from '@/composables/useAuthLogin'
import { useAuth } from '@/stores/auth'

const { LANG_ENABLED } = CONFIG

// Configuration
const { APP_LOGO, BASE_COLOR } = CONFIG
const { t } = useI18n()
const { toggleLocale } = useLocale()

// Auth composable - handles all authentication logic
const { isLoading, isSubmitting, handleLogin, handleOtpSubmit } = useAuthLogin()

// Get showOtpModal directly from store to avoid rerender issues
const auth = useAuth()

// Form state
const username = ref('')
const password = ref('')
const isShowPass = ref(false)
const usernameError = ref('')
const passwordError = ref('')

// Computed properties
const isFormValid = computed(() => {
  return (
    username.value.length >= 3 &&
    username.value.length <= 50 &&
    password.value.length > 0 &&
    !usernameError.value &&
    !passwordError.value
  )
})

// Methods
const showPass = (): void => {
  isShowPass.value = !isShowPass.value
}

const completeHandler = async (value: string): Promise<void> => {
  await handleOtpSubmit(value)
}

// Validation methods
const validateUsername = (): boolean => {
  if (!username.value) {
    usernameError.value = t('validation.username')
    return false
  }
  if (username.value.length < 3) {
    usernameError.value = t('validation.usernameMin')
    return false
  }
  if (username.value.length > 50) {
    usernameError.value = t('validation.usernameMax')
    return false
  }
  usernameError.value = ''
  return true
}

const validatePassword = (): boolean => {
  if (!password.value) {
    passwordError.value = t('validation.password')
    return false
  }
  passwordError.value = ''
  return true
}

const formHandler = async (e: Event): Promise<void> => {
  e.preventDefault()

  // Reset errors
  usernameError.value = ''
  passwordError.value = ''

  // Validate
  const isUsernameValid = validateUsername()
  const isPasswordValid = validatePassword()

  if (!isUsernameValid || !isPasswordValid || !isFormValid.value) {
    return
  }

  // Call auth composable to handle login
  await handleLogin({
    username: username.value,
    password: password.value
  })

  // Clear form on success (composable handles navigation)
  // Form is cleared after successful navigation
}
</script>
